import { gql, useQuery } from '@apollo/client';
import { AlertTriangleIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { SETTINGS_TAB_PARAM, SettingsTab } from '../../panes/settings/settingsTabs';
import { PATHS } from '../../paths';

import type { MembershipAccessQuery, MembershipAccessQueryVariables } from './__generated__/MembershipNotice.gql';

/**
 * Name of the query below, for the paths that refetch it.
 *
 * The notice has no push for a lapse, so a server-side refusal is the signal:
 * the assistant socket's `4004` close refetches through this, which puts the
 * notice on screen at the moment it is relevant rather than on a timer. A
 * completed checkout refetches it too, which is what clears the notice on a
 * renewal. Exported so callers name the document rather than carrying a string
 * literal that could drift from it.
 */
export const MEMBERSHIP_ACCESS_QUERY_NAME = 'MembershipAccess';

/** Whether the household may use the metered surface; shared with the assistant pane's paused state. */
export const MEMBERSHIP_ACCESS = gql`
  query MembershipAccess {
    getCurrentFamily {
      id
      hasMembershipAccess
      viewerCanManageBilling
    }
  }
`;

interface IBaseMembershipNoticeProps {
  /**
   * Whether the viewer can act on billing. A loved one cannot — their role is
   * denied every billing read — so they get no link, and the copy names who can.
   */
  canManageBilling?: boolean;
}

/**
 * Base visual component for the lapsed-membership notice.
 * Use this component directly when you need to control the state manually.
 * Wears the record-page alert pill treatment (`RecordAlertBannerBase`) so the
 * app-wide notices and the in-record banners read as one family.
 */
export const BaseMembershipNotice = ({ canManageBilling = true }: IBaseMembershipNoticeProps) => {
  return (
    <div className="bg-utility-error-50 border-utility-error-100 shadow-xs flex min-h-12 w-full items-center gap-2 rounded-2xl border px-4 py-2.5">
      <AlertTriangleIcon className="text-utility-error-500 size-[18px] shrink-0" />
      <span className="text-utility-error-700 min-w-0 flex-1 text-base font-medium">
        {canManageBilling
          ? 'Your Mantle membership isn’t active. Start it to use the assistant again.'
          : 'Your household’s Mantle membership isn’t active. Your caregiver can start it.'}
      </span>
      {canManageBilling && (
        <Link
          to={`${PATHS.SETTINGS}?${SETTINGS_TAB_PARAM}=${SettingsTab.Billing}`}
          className="text-utility-error-500 hover:text-utility-error-700 shrink-0 text-base font-medium underline hover:no-underline"
        >
          Go to billing
        </Link>
      )}
    </div>
  );
};

/**
 * Connected component that reads the household's membership access.
 *
 * Renders nothing until the answer is in hand: the field is `true` for every
 * household the wall is switched off for, so a missing answer is far more
 * likely to mean "still loading" than "lapsed", and a notice that flashed on
 * every page load would be worse than one that arrives a tick late.
 */
const MembershipNotice = () => {
  const { data, refetch } = useQuery<MembershipAccessQuery, MembershipAccessQueryVariables>(MEMBERSHIP_ACCESS, {
    fetchPolicy: 'cache-and-network',
  });

  // No poll: a lapse is rare, and the refusals it would eventually catch each
  // refetch this query themselves ({@link MEMBERSHIP_ACCESS_QUERY_NAME}), which
  // shows the notice at the moment it matters instead of on a timer. This covers
  // the remaining case — a tab left open across the webhook, then returned to.
  //
  // Listening for the event rather than tracking visibility in state keeps the
  // mount out of it: the query above has already gone to the network by then, so
  // a state-seeded effect would fire a second identical read on every shell
  // mount, and the notice would re-render on every tab switch.
  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refetch();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refetch]);

  if (data?.getCurrentFamily?.hasMembershipAccess !== false) {
    return null;
  }

  return <BaseMembershipNotice canManageBilling={data.getCurrentFamily.viewerCanManageBilling} />;
};

export default MembershipNotice;
